import { memo, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  ComparisonPeak,
  ComparisonUnavailableReason,
  MatchedPair,
  PreprocessingKey,
  SpectrumComparisonData,
} from "@/types/spectrumComparison";
import { formatRt } from "@/utils/formatRt";
import { panelCls } from "@/utils/panelCls";

/**
 * Head-to-Tail 스펙트럼 비교 (S2 · 2026-08-18).
 *
 * 기획: `docs/2026/260818_표준물질DB화면_기획.md` §4.1.
 *
 * 원본 프로그램(NIST MS Search)의 본질이 이 화면이다 — 위(실험)와
 * 아래(참조)를 눈으로 맞춰 감정인이 판단한다.
 *
 * 이 컴포넌트가 지키는 것:
 *
 * * **참조 스펙트럼이 없으면 차트를 그리지 않는다.** 빈 아래쪽은 "일치하는
 *   것이 없다"로 읽힌다. 대신 왜 비교할 수 없는지 말하고, 그것이 불일치를
 *   뜻하지 않는다고 못 박는다.
 * * 점수는 **산출식·정규화·허용오차·사용 피크 수와 함께** 낸다.
 * * 검색 범위(참조 몇 종 중)를 점수 옆에 항상 붙인다 — "무엇이든 찾아준다"는
 *   기대를 만들지 않기 위해서다.
 * * 배경가스를 거른 값과 안 거른 값을 **둘 다** 볼 수 있고, 기본은 원자료다.
 *   전처리를 몰래 적용하지 않는다.
 * * **판정하지 않는다.** 임계값도 "유사/불일치" 문구도 없다.
 */

export interface MirrorPoint {
  mz: number;
  /** 실험 강도 (base peak = 100). */
  experiment: number;
  /** 참조 강도 (base peak = -100). 아래쪽으로 뒤집는다. */
  reference: number;
}

const EXPERIMENT_COLOR = "#dc2626"; // 실험 — 붉은 계열
const REFERENCE_COLOR = "#2563eb"; // 참조 — 푸른 계열

/**
 * 그림용 m/z 자릿수.
 *
 * 실측 스펙트럼의 m/z 는 `33.96`·`34.05` 처럼 촘촘하다. 그대로 그리면 Recharts
 * 가 numeric X축의 막대 밴드를 0 에 가깝게 잡아 **막대가 아예 보이지 않는다**
 * (2026-08-19 프로덕션에서 실제로 발생 — 막대 폭 0.01px).
 * 기존 Mass Spectrum 차트는 백엔드가 소수 1자리로 반올림해 보내 문제가 없었다.
 *
 * **점수 계산은 백엔드가 원값으로 한다.** 여기서 맞추는 것은 그림뿐이고,
 * m/z 값 표는 원값을 유지한다(보고서 인용값이라 반올림할 수 없다).
 */
const DISPLAY_MZ_DIGITS = 1;

/** 질량 스펙트럼 막대(스템)의 고정 폭(px). */
const STEM_WIDTH = 2;

/**
 * 스펙트럼 막대를 **고정 폭으로 직접 그린다.**
 *
 * Recharts 는 numeric X축에서 막대 폭을 데이터 간격으로 눌러버린다. 실측
 * 스펙트럼처럼 m/z 가 촘촘하면 `barSize` 를 줘도 무시되고 0.06px 까지 줄어
 * **막대가 보이지 않는다**(2026-08-19 프로덕션 실측: Head-to-Tail·Side by
 * Side 0.06px, 차이 0.12px).
 *
 * 질량 스펙트럼은 원래 폭이 없는 **선 스펙트럼**이므로, 밴드 계산에 기대지
 * 않고 중심 좌표에 고정 폭으로 긋는 것이 물리적으로도 옳다.
 */
function SpectrumStem(props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
}) {
  const { x = 0, y = 0, width = 0, height = 0, fill } = props;
  const center = x + width / 2;
  // 높이 0(강도 0)인 피크는 그리지 않는다 — 축 위 점으로 남으면 오독된다.
  if (!Number.isFinite(center) || Math.abs(height) < 0.5) return null;
  return (
    <rect
      x={center - STEM_WIDTH / 2}
      y={height >= 0 ? y : y + height}
      width={STEM_WIDTH}
      height={Math.abs(height)}
      fill={fill}
    />
  );
}

function quantizeMz(mz: number): number {
  const factor = 10 ** DISPLAY_MZ_DIGITS;
  return Math.round(mz * factor) / factor;
}

/**
 * 그림용 상대강도 맵. 키는 **양자화된 m/z** 다.
 *
 * 같은 자리로 반올림돼 겹치는 피크는 **큰 쪽을 남긴다**(스펙트럼 관례 —
 * 합산하면 없던 큰 피크가 생긴다).
 */
function normalizeToBasePeak(peaks: ComparisonPeak[]): Map<number, number> {
  const byMz = new Map<number, number>();
  let base = 0;
  for (const peak of peaks) {
    if (!Number.isFinite(peak.mz) || !Number.isFinite(peak.intensity)) continue;
    const mz = quantizeMz(peak.mz);
    byMz.set(mz, Math.max(byMz.get(mz) ?? 0, peak.intensity));
  }
  for (const intensity of byMz.values()) base = Math.max(base, intensity);
  if (base <= 0) {
    // 강도가 전부 0 이어도 0 으로 나누지 않는다.
    return new Map([...byMz].map(([mz]) => [mz, 0]));
  }
  return new Map([...byMz].map(([mz, i]) => [mz, (i / base) * 100]));
}

/**
 * 미러 차트용 계열을 만든다.
 *
 * 양쪽을 **각자의 base peak 로 정규화**한다. 참조 DB 의 강도 스케일이
 * 1000·999·raw counts 로 제각각이라, 정규화하지 않으면 한쪽이 보이지 않는다.
 * 참조는 음수로 뒤집어 아래쪽에 그린다.
 */
export function buildMirrorSeries(
  experimentPeaks: ComparisonPeak[],
  referencePeaks: ComparisonPeak[],
): MirrorPoint[] {
  const experiment = normalizeToBasePeak(experimentPeaks);
  const reference = normalizeToBasePeak(referencePeaks);
  const allMz = new Set<number>([...experiment.keys(), ...reference.keys()]);
  return (
    [...allMz]
      .sort((a, b) => a - b)
      .map((mz) => {
        const ref = reference.get(mz) ?? 0;
        return {
          mz,
          experiment: experiment.get(mz) ?? 0,
          // 없는 피크는 0 이다. `-0` 을 남기지 않는다.
          reference: ref === 0 ? 0 : -ref,
        };
      })
      // 양쪽 모두 0 인 m/z 는 그려지지도 않으면서 X축만 늘린다 — 스펙트럼이
      // 화면 한쪽에 뭉치는 원인이다(프로덕션 실측). m/z 값 표에는 남긴다.
      .filter((p) => p.experiment !== 0 || p.reference !== 0)
  );
}

/**
 * Side by Side 계열 — **양쪽 모두 위로** 뻗는다.
 *
 * 원본(NIST MS Search)의 Side by Side 는 위아래로 나뉜 두 차트가 아니라
 * **하나의 축에 실험(빨강)·참조(파랑) 막대를 나란히 세운 것**이다
 * (기획 v2 §1.2 — 1.PNG 확대 판독). 미러는 `Head to Tail` 쪽이다.
 */
export function buildSideBySideSeries(
  experimentPeaks: ComparisonPeak[],
  referencePeaks: ComparisonPeak[],
): MirrorPoint[] {
  return buildMirrorSeries(experimentPeaks, referencePeaks).map((point) => ({
    ...point,
    reference: Math.abs(point.reference),
  }));
}

export interface DifferencePoint {
  mz: number;
  /** 실험 상대강도 − 참조 상대강도. 양수면 실험에 더 많다. */
  difference: number;
}

/**
 * 차이(Difference) 계열을 만든다.
 *
 * **짝짓기는 백엔드가 준 `matchedPairs` 만 쓴다.** 화면이 따로 허용오차를
 * 적용하면 점수와 다른 기준이 생겨 숫자와 그림이 어긋난다.
 * 짝이 없는 피크는 상대편을 0 으로 두고 그대로 남는다 — 한쪽에만 있는
 * 이온이야말로 감정인이 봐야 할 것이다.
 */
export function buildDifferenceSeries(
  experimentPeaks: ComparisonPeak[],
  referencePeaks: ComparisonPeak[],
  matchedPairs: MatchedPair[],
): DifferencePoint[] {
  const experiment = normalizeToBasePeak(experimentPeaks);
  const reference = normalizeToBasePeak(referencePeaks);
  // 짝의 m/z 도 그림 기준으로 맞춘다 — 계열 키와 어긋나면 차이가 0 으로 보인다.
  const pairedExperiment = new Set(
    matchedPairs.map((p) => quantizeMz(p.experiment_mz)),
  );
  const pairedReference = new Set(
    matchedPairs.map((p) => quantizeMz(p.reference_mz)),
  );

  const points: DifferencePoint[] = matchedPairs.map((pair) => ({
    mz: quantizeMz(pair.experiment_mz),
    difference:
      (experiment.get(quantizeMz(pair.experiment_mz)) ?? 0) -
      (reference.get(quantizeMz(pair.reference_mz)) ?? 0),
  }));

  for (const [mz, value] of experiment) {
    if (!pairedExperiment.has(mz)) points.push({ mz, difference: value });
  }
  for (const [mz, value] of reference) {
    if (!pairedReference.has(mz)) points.push({ mz, difference: -value });
  }
  return points.filter((p) => p.difference !== 0).sort((a, b) => a.mz - b.mz);
}

export interface PeakTableRow {
  mz: number;
  /** 원자료 측정값 — 보고서에 인용할 값. */
  intensity: number;
  /** base peak = 100 기준 상대강도. */
  relative: number;
}

/** m/z 값 표의 행. 강도 내림차순 — 큰 피크부터 인용한다. */
export function buildPeakTable(peaks: ComparisonPeak[]): PeakTableRow[] {
  const relative = normalizeToBasePeak(peaks);
  const merged = new Map<number, number>();
  for (const peak of peaks) {
    if (!Number.isFinite(peak.mz) || !Number.isFinite(peak.intensity)) continue;
    merged.set(peak.mz, (merged.get(peak.mz) ?? 0) + peak.intensity);
  }
  return [...merged]
    .map(([mz, intensity]) => ({
      mz,
      intensity,
      relative: relative.get(mz) ?? 0,
    }))
    .sort((a, b) => b.intensity - a.intensity || a.mz - b.mz);
}

/**
 * 의미 있는 피크가 있는 m/z 구간을 찾는다 (원본 `Options → m/z range` 기본값).
 *
 * 실측 스캔은 미량 노이즈가 넓게 퍼져 있어 데이터 전 범위를 그대로 축으로
 * 쓰면 스펙트럼이 화면 한쪽에 뭉친다(프로덕션 실측: 상대강도 1% 이상은
 * m/z 31~81 인데 노이즈가 355 까지).
 *
 * **측정 피크를 지우는 것이 아니다** — 기본으로 보는 창을 정할 뿐이고,
 * 사용자는 언제든 전체 범위로 되돌릴 수 있다.
 */
export function significantMzRange(
  experimentPeaks: ComparisonPeak[],
  referencePeaks: ComparisonPeak[],
  thresholdPercent = 1,
): [number, number] {
  const collect = (peaks: ComparisonPeak[]) => {
    const usable = peaks.filter(
      (p) => Number.isFinite(p.mz) && Number.isFinite(p.intensity),
    );
    const base = Math.max(0, ...usable.map((p) => p.intensity));
    if (base <= 0) return usable.map((p) => p.mz);
    const strong = usable
      .filter((p) => (p.intensity / base) * 100 >= thresholdPercent)
      .map((p) => p.mz);
    return strong.length > 0 ? strong : usable.map((p) => p.mz);
  };

  const mzs = [...collect(experimentPeaks), ...collect(referencePeaks)];
  if (mzs.length === 0) return [0, 100];
  const lo = Math.max(0, Math.floor(Math.min(...mzs) - 5));
  const hi = Math.ceil(Math.max(...mzs) + 5);
  return [lo, hi > lo ? hi : lo + 10];
}

type ViewMode = "head-to-tail" | "side-by-side" | "difference";

const VIEW_LABEL: Record<ViewMode, string> = {
  "head-to-tail": "Head-to-Tail",
  "side-by-side": "Side by Side",
  difference: "차이",
};

const UNAVAILABLE_MESSAGE: Record<ComparisonUnavailableReason, string> = {
  no_reference_compound:
    "이 화합물은 참조 DB에 등록돼 있지 않습니다. 비교할 대상이 없습니다.",
  no_reference_spectrum:
    "참조물질로는 등록돼 있으나 실측 스펙트럼을 보유하고 있지 않습니다.",
  no_experiment_spectrum:
    "이 시료에는 질량 스펙트럼(MS) 스캔이 없습니다. 비교할 실험 자료가 없습니다.",
  background_only_spectrum:
    "이 스캔은 배경가스 피크만 있어 '배경가스 제외' 조건으로는 비교 입력이 " +
    "비어 있습니다. 원자료 보기에서는 표시됩니다.",
  comparison_rejected:
    "스펙트럼 강도를 읽을 수 없어 비교를 수행하지 않았습니다.",
};

const PREPROCESSING_LABEL: Record<PreprocessingKey, string> = {
  none: "원자료",
  background_excluded: "배경가스 제외",
};

interface SpectrumComparisonProps {
  comparison: SpectrumComparisonData | null | undefined;
  isLoading?: boolean;
  height?: number;
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className={panelCls("standard", "h-full")}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center flex-wrap">
        <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
        <span>스펙트럼 비교</span>
        <span className="ml-2 text-sm font-normal text-gray-500">
          (Head-to-Tail)
        </span>
      </h3>
      {children}
    </div>
  );
}

function SpectrumChart({
  children,
  height,
}: {
  children: React.ReactElement;
  height: number;
}) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      {children}
    </ResponsiveContainer>
  );
}

/** 격자 한 행에 담는 `m/z 상대강도` 쌍 수. 원본은 5쌍이다. */
const PAIRS_PER_ROW = 4;

/**
 * m/z 값 표 — 원본과 같은 **쌍 격자** 서식.
 *
 * 원본은 `39 32| 50 59| 51 72| 52 59| 53 19|` 처럼 쌍을 여러 열로 늘어놓는다.
 * 한 피크에 한 줄을 쓰면 137피크 스펙트럼이 세로로 137줄이 되어 한눈에 들어오지
 * 않는다(기획 v2 §5.2-3).
 *
 * 강도는 base peak = 100 기준 상대강도다. **원자료 측정값은 셀 툴팁으로
 * 남긴다** — 보고서에 인용할 값이라 버릴 수 없다.
 */
function PeakTable({
  caption,
  rows,
  color,
}: {
  caption: string;
  rows: PeakTableRow[];
  color: string;
}) {
  const grouped: PeakTableRow[][] = [];
  for (let i = 0; i < rows.length; i += PAIRS_PER_ROW) {
    grouped.push(rows.slice(i, i + PAIRS_PER_ROW));
  }

  return (
    <div className="max-h-56 overflow-y-auto rounded border border-gray-200">
      <table className="w-full text-[11px] tabular-nums">
        <caption className="sr-only">{caption}</caption>
        <tbody>
          {grouped.map((group) => (
            <tr
              key={`row-${group[0]?.mz}`}
              className="border-t border-gray-100 first:border-t-0"
            >
              {group.map((row) => (
                <td
                  key={row.mz}
                  className="whitespace-nowrap px-2 py-0.5"
                  title={`m/z ${row.mz} · 상대강도 ${row.relative.toFixed(1)} · 측정값 ${row.intensity}`}
                >
                  <span style={{ color }}>{row.mz}</span>{" "}
                  <span className="text-gray-700">
                    {Math.round(row.relative)}
                  </span>
                  <span className="text-gray-300">|</span>
                </td>
              ))}
              {group.length < PAIRS_PER_ROW &&
                Array.from({ length: PAIRS_PER_ROW - group.length }).map(
                  (_, i) => (
                    <td
                      // biome-ignore lint/suspicious/noArrayIndexKey: 빈 칸 채우기
                      key={`pad-${i}`}
                      className="px-2 py-0.5"
                    />
                  ),
                )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SpectrumComparison = ({
  comparison,
  isLoading = false,
  height = 320,
}: SpectrumComparisonProps) => {
  const [preprocessing, setPreprocessing] = useState<PreprocessingKey>("none");
  const [view, setView] = useState<ViewMode>("head-to-tail");
  /** 그림에 보이는 m/z 창. null 이면 의미 있는 구간을 자동으로 잡는다. */
  const [mzRange, setMzRange] = useState<[number, number] | null>(null);

  const variant = comparison?.variants?.[preprocessing] ?? null;
  const similarity = variant?.similarity ?? null;
  const exclusion = variant?.exclusion ?? null;

  /**
   * 선택한 전처리 조건이 그림과 표에도 그대로 적용된다.
   * 점수는 거른 값인데 그림은 안 거른 값이면 같은 화면에서 서로 다른 말을 한다.
   */
  const excludedMz = useMemo(
    () =>
      preprocessing === "background_excluded" && exclusion
        ? new Set(exclusion.excluded_mz)
        : null,
    [preprocessing, exclusion],
  );

  const keep = useMemo(
    () => (peaks: ComparisonPeak[]) =>
      excludedMz
        ? peaks.filter((p) => !excludedMz.has(Math.round(p.mz)))
        : peaks,
    [excludedMz],
  );

  const experimentPeaks = useMemo(
    () => keep(comparison?.experiment?.peaks ?? []),
    [comparison, keep],
  );
  const referencePeaks = useMemo(
    () => keep(comparison?.reference?.peaks ?? []),
    [comparison, keep],
  );

  const mirrorSeries = useMemo(
    () => buildMirrorSeries(experimentPeaks, referencePeaks),
    [experimentPeaks, referencePeaks],
  );
  const sideBySideSeries = useMemo(
    () => buildSideBySideSeries(experimentPeaks, referencePeaks),
    [experimentPeaks, referencePeaks],
  );
  const differenceSeries = useMemo(
    () =>
      buildDifferenceSeries(
        experimentPeaks,
        referencePeaks,
        similarity?.matched_pairs ?? [],
      ),
    [experimentPeaks, referencePeaks, similarity],
  );
  // 원본 격자는 m/z 오름차순이다 — 특정 이온을 찾아 읽는 표이므로.
  const autoRange = useMemo(
    () => significantMzRange(experimentPeaks, referencePeaks),
    [experimentPeaks, referencePeaks],
  );
  const fullRange = useMemo((): [number, number] => {
    const mzs = [...experimentPeaks, ...referencePeaks].map((p) => p.mz);
    if (mzs.length === 0) return [0, 100];
    return [
      Math.max(0, Math.floor(Math.min(...mzs) - 5)),
      Math.ceil(Math.max(...mzs) + 5),
    ];
  }, [experimentPeaks, referencePeaks]);
  const activeRange = mzRange ?? autoRange;

  const experimentRows = useMemo(
    () => [...buildPeakTable(experimentPeaks)].sort((a, b) => a.mz - b.mz),
    [experimentPeaks],
  );
  const referenceRows = useMemo(
    () => [...buildPeakTable(referencePeaks)].sort((a, b) => a.mz - b.mz),
    [referencePeaks],
  );

  if (isLoading) {
    return (
      <Frame>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center">
          <p className="text-sm text-gray-500">불러오는 중…</p>
        </div>
      </Frame>
    );
  }

  if (!comparison) {
    return (
      <Frame>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center">
          {/* 2026-08-28: 옛 문구 "후보 목록에서" 는 같은 화면의 두 표(AI 동정 후보 /
              참조 스펙트럼 순위) 중 어느 쪽인지 알 수 없었다. 실제 조작 대상은
              AI 동정 후보 표다 — 그 표의 선택이 result_id 를 정하고, 이 비교는
              그 result_id 에 종속된다. */}
          <p className="text-sm text-gray-500">
            「AI 동정 후보」 표에서 물질을 선택하면 참조 스펙트럼과 비교합니다.
          </p>
        </div>
      </Frame>
    );
  }

  // ---- 비교 불가: 차트를 그리지 않는다.
  if (!comparison.comparable || !comparison.variants || !comparison.reference) {
    const reason = comparison.unavailable_reason;
    return (
      <Frame>
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-5">
          <p className="text-sm font-medium text-amber-900">
            {comparison.compound_name} — 비교할 수 없습니다
          </p>
          <p className="mt-2 text-sm text-amber-800">
            {reason
              ? UNAVAILABLE_MESSAGE[reason]
              : "비교에 필요한 자료가 없습니다."}
          </p>
          <p className="mt-3 text-xs text-amber-700">
            이것은 <strong>일치하는 물질이 없다는 뜻이 아닙니다</strong> —
            비교에 필요한 자료가 없다는 뜻입니다.
          </p>
          <p className="mt-1 text-xs text-amber-700">
            비교 대상 범위: 실측 참조 스펙트럼 보유{" "}
            {comparison.reference_library_size}종
          </p>
        </div>
      </Frame>
    );
  }

  const axis = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
      <XAxis
        dataKey="mz"
        type="number"
        domain={activeRange}
        allowDataOverflow
        label={{
          // position:"bottom" 은 하단 여백이 없으면 SVG 밖에 그려진다.
          // 아래 세 BarChart 의 margin.bottom 과 짝이다 (2026-09-06).
          value: "m/z",
          position: "insideBottom",
          offset: -8,
          style: { textAnchor: "middle", fill: "#64748b" },
        }}
        tick={{ fontSize: 12, fill: "#64748b" }}
      />
    </>
  );

  return (
    <Frame>
      {/* 헤더 — 점수와 검색 범위는 항상 붙어 다닌다 */}
      <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1 mb-3">
        <span className="text-base font-semibold text-gray-900">
          {comparison.reference.compound_name}
        </span>
        {similarity ? (
          <>
            <span className="text-sm text-gray-600">유사도</span>
            <span className="text-2xl font-bold tabular-nums text-indigo-700">
              {similarity.score.toFixed(3)}
            </span>
          </>
        ) : null}
        <span className="text-xs text-gray-500">
          참조 {comparison.reference_library_size}종 중
        </span>
      </div>

      {/* 보기 방식 + 전처리 조건 */}
      <div className="flex flex-wrap gap-4 mb-3">
        <div className="flex gap-2">
          {(Object.keys(VIEW_LABEL) as ViewMode[]).map((key) => (
            <button
              type="button"
              key={key}
              onClick={() => setView(key)}
              className={`px-3 py-1 text-xs rounded border ${
                view === key
                  ? "bg-gray-800 text-white border-gray-800"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {VIEW_LABEL[key]}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {(Object.keys(PREPROCESSING_LABEL) as PreprocessingKey[]).map(
            (key) => (
              <button
                type="button"
                key={key}
                onClick={() => setPreprocessing(key)}
                className={`px-3 py-1 text-xs rounded border ${
                  preprocessing === key
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {PREPROCESSING_LABEL[key]}
              </button>
            ),
          )}
        </div>
      </div>

      {/* m/z 범위 — 원본 Options → m/z range. 측정 피크를 지우지 않는다 */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-gray-600">
        <span>m/z 범위</span>
        <label className="sr-only" htmlFor="mz-from">
          m/z 시작
        </label>
        <input
          id="mz-from"
          type="number"
          value={activeRange[0]}
          onChange={(e) => setMzRange([Number(e.target.value), activeRange[1]])}
          className="w-20 rounded border border-gray-300 px-1.5 py-0.5"
        />
        <span>~</span>
        <label className="sr-only" htmlFor="mz-to">
          m/z 끝
        </label>
        <input
          id="mz-to"
          type="number"
          value={activeRange[1]}
          onChange={(e) => setMzRange([activeRange[0], Number(e.target.value)])}
          className="w-20 rounded border border-gray-300 px-1.5 py-0.5"
        />
        <button
          type="button"
          onClick={() => setMzRange(fullRange)}
          className="rounded border border-gray-300 bg-white px-2 py-0.5 hover:bg-gray-50"
        >
          전체 범위 ({fullRange[0]}~{fullRange[1]})
        </button>
        <button
          type="button"
          onClick={() => setMzRange(null)}
          className="rounded border border-gray-300 bg-white px-2 py-0.5 hover:bg-gray-50"
        >
          자동
        </button>
        <span className="text-[11px] text-gray-500">
          — 측정 피크를 지우지 않고 <strong>보는 범위만</strong> 바꿉니다.
        </span>
      </div>

      {/* 이 조건에서 계산할 수 없을 때 — 0.00 을 만들지 않는다 */}
      {!variant?.computed && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          이 조건에서는 비교할 수 없습니다
          {variant?.not_computed_reason
            ? ` (${variant.not_computed_reason})`
            : ""}
          . 다른 조건의 점수를 확인하세요.
        </div>
      )}

      {/* 참조의 기준 피크가 사라진 경우 경고 */}
      {exclusion?.reference_base_peak_removed && (
        <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          이 조건에서는 참조 화합물의 기준 피크(base peak)가 제외됐습니다. 주된
          신호를 버린 채 계산된 점수이므로 그대로 읽으면 안 됩니다.
        </div>
      )}

      {/* 범례 */}
      <div className="flex items-center gap-4 mb-1 text-xs text-gray-600">
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ backgroundColor: EXPERIMENT_COLOR }}
          />
          <span>{view === "head-to-tail" ? "실험 (위)" : "실험"}</span>
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ backgroundColor: REFERENCE_COLOR }}
          />
          <span>{view === "head-to-tail" ? "참조 (아래)" : "참조"}</span>
        </span>
      </div>

      {view === "head-to-tail" && (
        <SpectrumChart height={height}>
          <BarChart
            data={mirrorSeries}
            stackOffset="sign"
            margin={{ top: 5, right: 5, bottom: 20, left: 5 }}
          >
            {axis}
            <YAxis
              domain={[-110, 110]}
              ticks={[-100, -50, 0, 50, 100]}
              tickFormatter={(v: number) => String(Math.abs(v))}
              label={{
                value: "상대강도 (%)",
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle", fill: "#64748b" },
              }}
              tick={{ fontSize: 12, fill: "#64748b" }}
            />
            <ReferenceLine y={0} stroke="#94a3b8" />
            <Tooltip
              labelFormatter={(label) => `m/z: ${label}`}
              formatter={(value?: number, name?: string) => [
                Math.abs(value ?? 0).toFixed(1),
                name === "experiment" ? "실험" : "참조",
              ]}
            />
            <Bar
              dataKey="experiment"
              fill={EXPERIMENT_COLOR}
              shape={<SpectrumStem />}
              isAnimationActive={false}
            />
            <Bar
              dataKey="reference"
              fill={REFERENCE_COLOR}
              shape={<SpectrumStem />}
              isAnimationActive={false}
            />
          </BarChart>
        </SpectrumChart>
      )}

      {view === "side-by-side" && (
        <SpectrumChart height={height}>
          <BarChart
            data={sideBySideSeries}
            barGap={1}
            margin={{ top: 5, right: 5, bottom: 20, left: 5 }}
          >
            {axis}
            <YAxis
              domain={[0, 110]}
              label={{
                value: "상대강도 (%)",
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle", fill: "#64748b" },
              }}
              tick={{ fontSize: 12, fill: "#64748b" }}
            />
            <Tooltip
              labelFormatter={(label) => `m/z: ${label}`}
              formatter={(value?: number, name?: string) => [
                (value ?? 0).toFixed(1),
                name === "experiment" ? "실험" : "참조",
              ]}
            />
            <Bar
              dataKey="experiment"
              fill={EXPERIMENT_COLOR}
              shape={<SpectrumStem />}
              isAnimationActive={false}
            />
            <Bar
              dataKey="reference"
              fill={REFERENCE_COLOR}
              shape={<SpectrumStem />}
              isAnimationActive={false}
            />
          </BarChart>
        </SpectrumChart>
      )}

      {view === "difference" && (
        <>
          <p className="mb-1 text-xs text-gray-600">
            실험 − 참조 (상대강도 차). 양수는 실험에만 더 있는 이온, 음수는
            참조에만 더 있는 이온입니다.
          </p>
          <SpectrumChart height={height}>
            <BarChart
              data={differenceSeries}
              margin={{ top: 5, right: 5, bottom: 20, left: 5 }}
            >
              {axis}
              <YAxis
                domain={[-110, 110]}
                label={{
                  value: "차이 (%)",
                  angle: -90,
                  position: "insideLeft",
                  style: { textAnchor: "middle", fill: "#64748b" },
                }}
                tick={{ fontSize: 12, fill: "#64748b" }}
              />
              <ReferenceLine y={0} stroke="#94a3b8" />
              <Tooltip labelFormatter={(label) => `m/z: ${label}`} />
              <Bar
                dataKey="difference"
                fill="#7c3aed"
                shape={<SpectrumStem />}
                isAnimationActive={false}
              />
            </BarChart>
          </SpectrumChart>
        </>
      )}

      {/* m/z 값 표 — 그래프만으로는 수치를 인용할 수 없다 */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <p className="mb-1 text-xs font-medium text-gray-700">
            실험 m/z 값 ({experimentRows.length}개)
          </p>
          <PeakTable
            caption="실험 m/z 값과 강도"
            rows={experimentRows}
            color={EXPERIMENT_COLOR}
          />
        </div>
        <div>
          <p className="mb-1 text-xs font-medium text-gray-700">
            참조 m/z 값 ({referenceRows.length}개)
          </p>
          <PeakTable
            caption="참조 m/z 값과 강도"
            rows={referenceRows}
            color={REFERENCE_COLOR}
          />
        </div>
      </div>
      <p className="mt-1 text-[11px] text-gray-500">
        각 칸은 <code>m/z 상대강도</code> 입니다. 상대강도는 각 스펙트럼의 base
        peak 를 100 으로 둔 값이고, 원자료 측정값은 칸에 마우스를 올리면
        나옵니다.
      </p>

      {/* 근거 — 점수만 떼어 놓지 않는다 */}
      <div className="mt-4 space-y-1 text-xs text-gray-600">
        {similarity && (
          <>
            <p>
              계산: 가중치 없는 코사인 유사도 · <span>base peak 정규화</span> ·
              m/z 허용오차 ±{similarity.mz_tolerance}
            </p>
            <p className="font-mono text-[11px] text-gray-500">
              {similarity.formula}
            </p>
            <p>
              짝지어진 피크 {similarity.matched_peak_count} · 실험{" "}
              {similarity.experiment_peak_count} · 참조{" "}
              {similarity.reference_peak_count}
            </p>
          </>
        )}
        {exclusion && (
          <p>
            제외한 배경가스 m/z {exclusion.excluded_mz.join(", ")} — 실험{" "}
            {exclusion.experiment_peaks_removed}개 · 참조{" "}
            {exclusion.reference_peaks_removed}개 제외
          </p>
        )}
        {comparison.experiment?.retention_time != null && (
          <p>
            실험 RT {formatRt(comparison.experiment.retention_time)} min
            {comparison.experiment.requested_retention_time != null &&
              comparison.experiment.requested_retention_time !==
                comparison.experiment.retention_time &&
              ` (후보에 기록된 RT ${formatRt(comparison.experiment.requested_retention_time)})`}
          </p>
        )}
        {/* T1: 같은 스캔 후보 묶음 — 실험 스펙트럼은 후보가 아니라 스캔의 것 */}
        {comparison.observation &&
          comparison.observation.candidate_count > 1 && (
            <p>
              같은 스캔 후보 {comparison.observation.candidate_count}개:{" "}
              {comparison.observation.candidates
                .map((c) => c.compound_name)
                .join(" · ")}{" "}
              — 실험 스펙트럼이 같으므로 후보끼리는 독립 검출이 아닙니다.
            </p>
          )}
        <p>
          참조 출처: {comparison.reference.source ?? "출처 미기재"} · 피크{" "}
          {comparison.reference.peak_count}개
        </p>
        <p className="text-gray-500">
          NIST Match factor 와는 다른 값이며 서로 환산되지 않습니다.
        </p>
      </div>
    </Frame>
  );
};

export default memo(SpectrumComparison);
